import { AgendaSlotType, isFullvisioSlotType } from '@omecare/utils';

/**
 * Slot medical-assistant (AA/SA) presence predicates.
 *
 * SA/AA ("Sans assistant·e" / "Avec assistant·e") is inferred at read time from
 * the assistant planning, but a slot is "sans assistant" (SA) whenever:
 * - it is explicitly pinned SA via `forceWithoutAssistant`, or
 * - it is a remote full-video (`fullvisio`) slot — remote doctors work alone.
 *
 * These helpers bundle every SA exception in one place so all UI surfaces
 * (calendar chips, list cards, detail sheets) decide consistently — add future
 * overrides here, not at each call site.
 */

/**
 * Whether a slot is necessarily "sans assistant·e" (SA), regardless of the
 * assistant planning.
 *
 * @param params.forceWithoutAssistant - The slot's force-SA override flag.
 * @param params.slotType - The slot's consultation mode (`fullvisio` ⇒ SA).
 * @returns `true` when the slot is forced SA (explicit override or remote video).
 */
export function isSlotForcedWithoutAssistant(params: {
  forceWithoutAssistant: boolean | null | undefined;
  slotType?: AgendaSlotType | null;
}): boolean {
  return (
    params.forceWithoutAssistant === true ||
    isFullvisioSlotType(params.slotType)
  );
}

/**
 * Whether a slot is effectively "avec assistant·e" (AA).
 *
 * This is the single source of truth for AA/SA across the UI — the assistant
 * indicators and the assistant-hours section all derive from it. A slot that is
 * pinned SA or is a remote full-video slot is never AA, regardless of the
 * resolved/inferred `withAssistant` value.
 *
 * @param params.withAssistant - The resolved/inferred assistant presence.
 * @param params.forceWithoutAssistant - The slot's force-SA override flag.
 * @param params.slotType - The slot's consultation mode (`fullvisio` ⇒ SA).
 * @returns `true` when the slot is AA, `false` when SA.
 */
export function isSlotWithAssistant(params: {
  withAssistant: boolean | null | undefined;
  forceWithoutAssistant: boolean | null | undefined;
  slotType?: AgendaSlotType | null;
}): boolean {
  return (
    params.withAssistant === true &&
    !isSlotForcedWithoutAssistant({
      forceWithoutAssistant: params.forceWithoutAssistant,
      slotType: params.slotType,
    })
  );
}
